/**
 * ================================================================================================
 *  THE TWO FLAGS, DRAWN RATHER THAN TYPED. **2026-10-25.**
 * ================================================================================================
 *
 * Owner: *"כפתור עברית וכפתור אנגלית אני רוצה לראות דגל ישראל ודגל ארה״ב במקום"*.
 *
 * ── WHY SVG AND NOT THE FLAG EMOJI ──────────────────────────────────────────────────────────────
 *
 * 🇮🇱 and 🇺🇸 are regional-indicator pairs, and **Windows does not render them as flags** — it draws
 * the letters, so the Owner would have seen `IL` and `US` on his own machine. The product is used on
 * Windows, so the flags are drawn.
 *
 * ── THE LABEL IS STILL THE LANGUAGE, AND THE FLAG IS DECORATION ─────────────────────────────────
 *
 * A flag names a COUNTRY, and a language is not a country: Hebrew is not "Israel" and English is
 * certainly not only "the United States". So the accessible name stays the language in its own
 * language — `עברית`, `English` — exactly as it was, and these SVGs are `aria-hidden`. A screen
 * reader hears the language; the eye gets the flag the Owner asked for.
 *
 * `viewBox` proportions are the official ratios (3:2 for Israel, 19:10 for the United States),
 * simplified to what reads at 20px: the stripes and the canton, without 50 stars nobody can see.
 */

export type FlagFor = 'he' | 'en';

export function LocaleFlag({ locale }: { readonly locale: FlagFor }): JSX.Element {
  return locale === 'he' ? <IsraelFlag /> : <UnitedStatesFlag />;
}

function IsraelFlag(): JSX.Element {
  return (
    <svg className="gp-flag" viewBox="0 0 30 20" aria-hidden="true" focusable="false">
      <rect width="30" height="20" fill="#ffffff" />
      <rect y="2.6" width="30" height="2.8" fill="#0038b8" />
      <rect y="14.6" width="30" height="2.8" fill="#0038b8" />
      {/*
        The Star of David as two overlaid triangles, stroked rather than filled: at this size a
        filled hexagram turns into a blob, and the outline is what makes it read as the star.
      */}
      <g fill="none" stroke="#0038b8" strokeWidth="1.1" strokeLinejoin="round">
        <path d="M15 6.4 18.4 12.3 11.6 12.3Z" />
        <path d="M15 13.6 11.6 7.7 18.4 7.7Z" />
      </g>
    </svg>
  );
}

function UnitedStatesFlag(): JSX.Element {
  return (
    <svg className="gp-flag" viewBox="0 0 38 20" aria-hidden="true" focusable="false">
      <rect width="38" height="20" fill="#ffffff" />
      {/*
        Seven red stripes of the thirteen. Drawn in a loop rather than written out, so the spacing
        is arithmetic and not thirteen hand-placed rectangles that drift apart.
      */}
      {[0, 2, 4, 6, 8, 10, 12].map((band) => (
        <rect key={band} y={(band * 20) / 13} width="38" height={20 / 13} fill="#b22234" />
      ))}
      <rect width="15.2" height={(7 * 20) / 13} fill="#3c3b6e" />
      {/* Nine suggested stars. Fifty would be a grey smudge at 20 pixels. */}
      <g fill="#ffffff">
        {[0, 1, 2].map((row) =>
          [0, 1, 2].map((col) => (
            <circle key={`${row}-${col}`} cx={3 + col * 4.6} cy={2.4 + row * 3.4} r="0.9" />
          )),
        )}
      </g>
    </svg>
  );
}
